import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import OverheadCategorySelect from '../shared/OverheadCategorySelect';
import SowSelect from '../shared/SowSelect';
import { canAccess } from '../../utils/rbac';
import { dailyCap, workedHours } from '../../utils/overtime';
import { isTimeEntryLocked, earliestEditable } from '../../utils/timeLock';
import { isOtherOverhead, OTHER_NOTE_MESSAGE } from '../../utils/overheads';
import {
  groupMilestonesForPicker,
  milestoneExpected,
  missingMilestoneReason,
  NO_MILESTONE_REASON_MESSAGE,
} from '../../utils/milestoneLogging';
import { buildBreakdown } from '../../utils/timeBreakdown';
import useTimeFavourites from '../../hooks/useTimeFavourites';
import FavouritesBar from '../shared/FavouritesBar';
import FeatureTour from '../shared/FeatureTour';
import Modal from '../shared/Modal';
import useFeatureTour from '../../hooks/useFeatureTour';
import { TIME_LOGGING_TOUR_ID, timeLoggingTourSteps } from './timeLoggingTour';

// A week's entries close at the end of the FOLLOWING Monday — they can't then
// be added, amended or deleted (preserves the data we're collecting). Admins,
// HR and PMO can still amend. The rule is in utils/timeLock.js, mirroring the
// authoritative server-side one in lib/timeLock.js.

// ── Date helpers (local-time safe) ───────────────────────────────────────────────
function toLocalISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function getMondayOf(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return toLocalISO(d);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toLocalISO(d);
}
function getMonthStart(date = new Date()) {
  return toLocalISO(new Date(date.getFullYear(), date.getMonth(), 1));
}
function getMonthEnd(date = new Date()) {
  return toLocalISO(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}
const today = toLocalISO(new Date());

// Logging time against a SOW that hasn't been issued yet is pre-formal-engagement
// work (writing the bid / building the SOW itself) — auto-tagged with this
// overhead category while keeping sow_id set, so the hours still roll up into
// that SOW's cost/profitability once it's issued and goes live.
const BID_SOW_CREATION_CATEGORY_CODE = 'BD_BID';

function fmtLong(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtShort(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function fmtMonth(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}
function fmtDow(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
}
function fmtWeekdayLong(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' });
}
function fmtDayNum(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').getDate();
}

function entryLabel(e) {
  if (e.task_name) return e.task_name;
  if (e.sow_reference) return `${e.sow_reference}${e.sow_detail ? ' — ' + e.sow_detail.slice(0, 50) : ''}`;
  if (e.overhead_category_name) return e.overhead_category_name;
  return 'Unnamed entry';
}

function typeColor(e) {
  // Written from approved leave, not logged by the person (src/lib/leaveTime.js).
  if (e.leave_request_id)     return { bg: 'bg-sky-50',    border: 'border-sky-200',    text: 'text-sky-800',    dot: 'bg-sky-400',    label: 'Leave'    };
  const isBidSow = e.sow_id && e.overhead_category_code === BID_SOW_CREATION_CATEGORY_CODE;
  if (e.task_id)              return { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-800',   dot: 'bg-blue-400',   label: 'Task'     };
  if (isBidSow)                return { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', dot: 'bg-indigo-400', label: 'Bid & SOW Creation' };
  if (e.sow_id && !e.task_id) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-400', label: 'Project' };
  return                             { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-800',  dot: 'bg-amber-400',  label: 'Overhead'  };
}

const fmt = n => typeof n === 'number' ? (n % 1 === 0 ? `${n}h` : `${n.toFixed(1)}h`) : '0h';

// The page chrome is deliberately the same shape as every other page's: the
// bordered button, the circular week arrows, the plain date beside them. Copied
// from the Bookings module (components/bookings/bookingsTheme.js and
// MyBookingsPage) rather than imported, so this page does not depend on that
// module's internals; the values are the ones to keep in step if that scale
// moves. This page had drifted to its own heading size, its own button radius
// and, alone in the app, a centred max-width column, which is what made it look
// like a different product.
const Btn = ({ active, className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`px-3 py-2 rounded-md border font-semibold text-[11px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      active ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    } ${className}`}
  />
);

// ── Entry card ────────────────────────────────────────────────────────────────
function EntryCard({ entry, onEdit, onDelete, locked }) {
  const c = typeColor(entry);
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${c.bg} ${c.border} group`}
      title={entry.leave_request_id ? 'From approved leave. Change or cancel the leave request and this follows it.' : undefined}>
      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-gray-800 truncate">{entryLabel(entry)}</div>
        {entry.description && <div className="text-[10px] text-gray-500 truncate">{entry.description}</div>}
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`text-[9px] font-bold ${c.text}`}>{c.label}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[12px] font-bold text-gray-700">{fmt(parseFloat(entry.hours))}</span>
        {/* The leave request is the record. Changing or cancelling it is what
            moves a leave entry, so it offers nothing to edit by hand; the card's
            tooltip says where to go instead. */}
        {entry.leave_request_id ? null : locked ? (
          <span className="text-[10px] text-gray-300" title="Closed — a week closes at the end of the following Monday">🔒</span>
        ) : (
          <div className="hidden group-hover:flex gap-1">
            <button onClick={() => onEdit(entry)} className="text-[10px] text-blue-500 hover:text-blue-700 font-semibold">Edit</button>
            <button onClick={() => onDelete(entry)} className="text-[10px] text-red-400 hover:text-red-600 font-semibold">Del</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Day column (week view) ─────────────────────────────────────────────────────
function DayColumn({ dateStr, entries, selected, onToggle, onAdd, onEdit, onDelete, isCurrentMonth = true, locked = false, cap = 0, absence = null }) {
  const isToday = dateStr === today;
  const dayEntries = entries[dateStr] || [];
  const dayTotal = dayEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  // Short is measured in hours worked. Leave written into the log is shown in
  // the day and its total, but the half day it covers is already off the cap.
  const dayWorked = workedHours(dayEntries);
  // A weekday (Mon-Fri) that's already here or past and under a full day is
  // "short" — flag it so missing time is obvious and one tap away.
  //
  // Unless the person was not working that day (item 44). Leave, sickness and
  // bank holidays fill the day: no amber, no "log the missing days", no
  // suggestion that something is owed. That is what "fills the day without
  // counting as work" means on this screen — the day reads as accounted for, and
  // the hours stay at nought.
  const dow = new Date(dateStr + 'T00:00:00').getDay();
  // A half-day absence excuses half the day, not all of it: the cap drops to
  // half rather than the day being written off, so the worked half is still
  // chased if it goes unlogged.
  const halfAbsence = absence?.slot === 'am' || absence?.slot === 'pm';
  const dayCap = halfAbsence ? cap / 2 : cap;
  const isShort = (!absence || halfAbsence) && dayCap > 0 && dow >= 1 && dow <= 5 && dateStr <= today && dayWorked < dayCap - 0.001;

  return (
    <div className={`flex flex-col min-w-0 ${!isCurrentMonth ? 'opacity-40' : ''}`}>
      <div
        onClick={() => onToggle(dateStr)}
        className={`flex items-center justify-between px-2 py-1.5 rounded-t-lg cursor-pointer select-none border-b-2 transition-colors ${
          selected
            ? 'bg-navy text-white border-navy'
            : isToday
            ? 'bg-blue-50 text-navy border-blue-300'
            : isShort
            ? 'bg-amber-50 text-amber-800 border-amber-300'
            : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
        }`}
      >
        <div>
          <div className={`text-[9px] font-bold uppercase ${selected ? 'text-white/70' : 'text-gray-400'}`}>{fmtDow(dateStr)}</div>
          <div className={`text-[13px] font-black ${selected ? 'text-white' : isToday ? 'text-blue-600' : 'text-gray-700'}`}>{fmtDayNum(dateStr)}</div>
        </div>
        {(dayTotal > 0 || isShort) && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${selected ? 'bg-white/20 text-white' : isShort ? 'bg-amber-200 text-amber-800' : 'bg-navy/10 text-navy'}`}
            title={isShort ? `${fmt(dayWorked)} of a ${fmt(dayCap)} day` : ''}>
            {isShort ? `${fmt(dayWorked)}/${fmt(dayCap)}` : fmt(dayTotal)}
          </span>
        )}
      </div>
      <div className="flex-1 space-y-1 pt-1 pb-2 min-h-[60px]">
        {/* The absence itself, drawn as what it is rather than as a time entry:
            no hours, and a colour that is not the colour of logged work. */}
        {absence && (
          <div className={`rounded px-1.5 py-1 border text-[8px] font-semibold ${
            absence.kind === 'sickness' ? 'bg-red-50 border-red-200 text-red-700'
            : absence.kind === 'bank_holiday' ? 'bg-gray-50 border-gray-200 text-gray-500'
            : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
            {absence.label}
            <div className="text-[7px] font-normal opacity-70">
              {halfAbsence ? 'Half the day; the rest is working time' : 'Not a working day'}
            </div>
          </div>
        )}
        {dayEntries.map(e => (
          <EntryCard key={e.id} entry={e} onEdit={onEdit} onDelete={onDelete} locked={locked} />
        ))}
      </div>
      {locked ? (
        <div className="text-[9px] text-gray-300 font-semibold py-1 text-center w-full select-none" title="Closed — a week closes at the end of the following Monday">🔒 Locked</div>
      ) : (
        <button
          onClick={() => onAdd([dateStr])}
          className={`text-[9px] font-semibold py-1 rounded transition-colors text-center w-full ${isShort ? 'text-amber-700 hover:bg-amber-100' : 'text-gray-400 hover:text-navy hover:bg-gray-50'}`}
        >
          {isShort ? '+ Add time' : '+ Add'}
        </button>
      )}
    </div>
  );
}

// ── Entry form modal ───────────────────────────────────────────────────────────
function EntryModal({ form, setForm, onSave, onClose, saving, error, tasks, sows, overheads, milestones, isEdit, minDate, entryMap, cap, excludeEntryId, favourites, sowFavourites, isFav, toggleFav }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const current = form.type === 'task' && form.task_id ? { kind: 'task', ref_id: String(form.task_id) }
    : form.type === 'sow' && form.sow_id ? { kind: 'sow', ref_id: String(form.sow_id) }
    : form.type === 'overhead' && form.overhead_category_id ? { kind: 'overhead', ref_id: String(form.overhead_category_id) }
    : null;
  const pickFavourite = (fav) => {
    if (fav.kind === 'task') { set('type', 'task'); set('task_id', fav.ref_id); }
    else if (fav.kind === 'sow') { set('type', 'sow'); set('sow_id', fav.ref_id); }
    else { set('type', 'overhead'); set('overhead_category_id', fav.ref_id); }
  };
  const currentLabel = !current ? ''
    : current.kind === 'task' ? (tasks.find(t => String(t.id) === current.ref_id)?.name || '')
    : current.kind === 'sow'  ? (sows.find(s => String(s.id) === current.ref_id)?.reference || '')
    : (overheads.find(o => String(o.id) === current.ref_id)?.name || '');

  // "Rest of day" only makes sense for a single date — with several dates
  // selected there's no one day to compute a remainder against.
  const singleDate = form.dates?.length === 1 ? form.dates[0] : null;
  const dayLogged = singleDate
    ? workedHours((entryMap[singleDate] || []).filter(e => e.id !== excludeEntryId))
    : 0;
  const remainder = singleDate ? Math.max(0, Math.round((cap - dayLogged) * 4) / 4) : 0;

  // An "Other" category needs a note, the same rule the quick panel applies.
  // Said on the field itself rather than only on the way out: being told what
  // was required after pressing Log is the worst moment to learn it.
  const isOther = form.type === 'overhead' && isOtherOverhead(overheads, form.overhead_category_id);

  const selectedSow = form.type === 'sow' ? sows.find(s => String(s.id) === String(form.sow_id)) : null;
  const isDraftSow = selectedSow?.status === 'Draft';
  const bidSowCategoryId = overheads.find(o => o.code === BID_SOW_CREATION_CATEGORY_CODE)?.id || '';

  // A not-yet-issued SOW has no formal engagement yet — time against it is bid/
  // SOW-creation work: non-billable (there's no client to bill), auto-tagged as
  // such while keeping sow_id set so it still rolls up into that SOW's cost later.
  // Skip on edit so re-opening an old entry doesn't silently re-derive its
  // category/billability from the SOW's now-current status.
  useEffect(() => {
    if (isEdit || form.type !== 'sow') return;
    const wantCategoryId = isDraftSow ? bidSowCategoryId : '';
    if ((form.overhead_category_id || '') !== wantCategoryId) set('overhead_category_id', wantCategoryId);
    const wantBillable = isDraftSow ? false : undefined;
    if (form.billable !== wantBillable) set('billable', wantBillable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, form.type, form.sow_id, isDraftSow, bidSowCategoryId]);

  // Milestones come from the page, not from here: the rule about logging without
  // one is applied on save, which happens in the parent.
  const { open: openMilestones, done: doneMilestones } = groupMilestonesForPicker(milestones);
  const milestoneLabel = (m) => `${m.milestone_code || 'Milestone'}${m.description ? ` — ${m.description.slice(0, 40)}` : ''}`;
  const milestoneUnset = form.type === 'sow' && !isDraftSow && milestoneExpected(milestones) && !form.milestone_id;
  const needsMilestoneReason = milestoneUnset
    && missingMilestoneReason({ milestones, milestoneId: form.milestone_id, notes: form.description });

  const removeDate = (d) => set('dates', form.dates.filter(x => x !== d));
  const addDateInput = (d) => {
    if (d && !form.dates.includes(d)) set('dates', [...form.dates, d].sort());
  };

  return (
    <Modal
      title={isEdit ? 'Edit Time Entry' : 'Add Time Entry'}
      onClose={onClose}
      dataTour="entry-modal"
      closeDataTour="entry-modal-close"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary text-[11px]">Cancel</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 text-[11px] font-bold rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Log${form.dates.length > 1 ? ` (${form.dates.length} days)` : ''}`}
          </button>
        </>
      }
    >
        {!isEdit && (
          <div className="-mx-4 -mt-3 mb-4 px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-[10px] text-blue-700">
            Log all the hours you've worked. <strong>Overtime, standby and call-outs are claimed on My Overtime</strong>, not here.
          </div>
        )}

        <div className="space-y-4">

          {/* Date(s) */}
          <div data-tour="entry-dates">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                {isEdit ? 'Date' : 'Date(s)'}
              </label>
              {singleDate && (
                <span className="text-[11px] font-bold text-navy">{fmtWeekdayLong(singleDate)}{singleDate === today ? ' (today)' : ''}</span>
              )}
            </div>
            {!isEdit && form.dates.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {form.dates.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 bg-navy/10 text-navy text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    {fmtDow(d)} {fmtShort(d)}
                    <button type="button" onClick={() => removeDate(d)} className="text-navy/50 hover:text-red-500 leading-none">×</button>
                  </span>
                ))}
              </div>
            )}
            {!isEdit && (() => {
              // Weekday quick-toggles for the week of the first selected date
              // (or today), so logging several days is one tap, not a repeated
              // date-picker dance.
              const base = new Date((form.dates[0] || today) + 'T00:00:00');
              const monday = new Date(base); monday.setDate(base.getDate() - ((base.getDay() + 6) % 7));
              const wk = Array.from({ length: 5 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d.toISOString().slice(0, 10); });
              const addable = wk.filter(d => d <= today && (!minDate || d >= minDate));
              const allSel = addable.length > 0 && addable.every(d => form.dates.includes(d));
              return (
                <div className="flex flex-wrap gap-1 mb-2">
                  {wk.map(d => {
                    const disabled = d > today || (minDate && d < minDate);
                    const sel = form.dates.includes(d);
                    return (
                      <button key={d} type="button" disabled={disabled}
                        onClick={() => sel ? removeDate(d) : addDateInput(d)}
                        title={fmtShort(d)}
                        className={`px-2 py-0.5 rounded-full text-[9px] font-semibold border ${sel ? 'bg-navy text-white border-navy' : disabled ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                        {fmtDow(d)}
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => set('dates', allSel ? form.dates.filter(d => !addable.includes(d)) : [...new Set([...form.dates, ...addable])].sort())}
                    className="px-2 py-0.5 rounded-full text-[9px] font-bold border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100">
                    {allSel ? 'Clear week' : 'This week (Mon–Fri)'}
                  </button>
                </div>
              );
            })()}
            <input
              type="date"
              value={isEdit ? form.dates[0] || '' : ''}
              min={minDate || undefined}
              max={today}
              onChange={e => isEdit ? set('dates', [e.target.value]) : addDateInput(e.target.value)}
              className="form-input"
              placeholder={!isEdit ? 'Add another date…' : undefined}
            />
            {minDate && (
              <div className="text-[9px] text-gray-400 mt-1">You can log/amend back to <strong>{fmtShort(minDate)}</strong> — older entries are locked.</div>
            )}
            {!isEdit && (
              <div className="text-[9px] text-gray-400 mt-1">Click the date picker to add multiple dates — each gets its own entry.</div>
            )}
          </div>

          {/* Favourites quick-pick */}
          {!isEdit && (
            <div data-tour="entry-favourites">
              <FavouritesBar
                favourites={favourites} tasks={tasks} sows={sows} overheads={overheads}
                onPick={pickFavourite}
                onRemove={(fav) => toggleFav(fav.kind, fav.ref_id)}
                current={current}
                isCurrentFav={current ? isFav(current.kind, current.ref_id) : false}
                onToggleCurrent={current ? () => toggleFav(current.kind, current.ref_id, currentLabel) : null}
                theme="navy"
              />
            </div>
          )}

          {/* Type toggle */}
          <div data-tour="entry-type">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
            <div className="flex rounded-lg border overflow-hidden">
              {[['task', 'Task'], ['sow', 'Project'], ['overhead', 'Overhead']].map(([t, label]) => (
                <button key={t} type="button" onClick={() => set('type', t)}
                  className={`flex-1 py-1.5 text-[11px] font-semibold transition-colors border-r last:border-r-0 ${
                    form.type === t ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Task / Project / Overhead selector */}
          {form.type === 'task' && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Task</label>
              <select className="form-input" value={form.task_id} onChange={e => set('task_id', e.target.value)}>
                <option value="">Select task…</option>
                {tasks.map(t => (
                  <option key={t.id} value={t.id}>{t.name}{t.project_name ? ` — ${t.project_name}` : ''}</option>
                ))}
              </select>
            </div>
          )}
          {form.type === 'sow' && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Project</label>
              <SowSelect sows={sows} value={form.sow_id} onChange={v => set('sow_id', v)} placeholder="Search projects…"
                favouriteIds={sowFavourites} onToggleFavourite={(id) => toggleFav('sow', id, sows.find(s => String(s.id) === String(id))?.reference || '')} />
            </div>
          )}
          {form.type === 'sow' && form.sow_id && isDraftSow && (
            <div className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2.5 py-2 leading-relaxed">
              <strong>Not yet issued</strong> — this SOW has no formal engagement yet, so this is pre-engagement work.
              It'll be logged as <strong>Bid &amp; SOW Creation</strong> and still tagged to this SOW, so the time counts toward its cost once it's issued.
            </div>
          )}
          {form.type === 'sow' && form.sow_id && !isDraftSow && milestoneExpected(milestones) && (
            <div data-tour="entry-milestone">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Milestone</label>
              <select
                className={`form-input ${milestoneUnset ? 'border-amber-400 focus:ring-amber-400' : ''}`}
                value={form.milestone_id || ''}
                onChange={e => set('milestone_id', e.target.value)}
              >
                <option value="">Select milestone…</option>
                {openMilestones.map(m => (
                  <option key={m.id} value={m.id}>{milestoneLabel(m)}</option>
                ))}
                {doneMilestones.length > 0 && (
                  <optgroup label="Already delivered">
                    {doneMilestones.map(m => (
                      <option key={m.id} value={m.id} className="text-gray-400">{milestoneLabel(m)} ({m.status})</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}
          {milestoneUnset && (
            <div
              data-tour="milestone-warning"
              className={`rounded border px-3 py-2.5 leading-relaxed ${
                needsMilestoneReason ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-amber-50/60 border-amber-200 text-amber-800'
              }`}
            >
              {needsMilestoneReason ? (
                <div className="flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">⚠️</span>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide">No milestone selected</div>
                    <div className="text-[10px] mt-1">
                      This project has milestones, and time logged against none of them
                      <strong> does not cost against the project</strong>.
                    </div>
                    <div className="text-[10px] mt-1">
                      Pick the milestone above. If there genuinely isn't one, <strong>write the reason in the notes
                      below</strong> — the entry is flagged for review.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[10px]">
                  <strong>No milestone</strong> — your note will be read as the reason, and this entry is flagged for
                  review on the Time Entry Audit.
                </div>
              )}
            </div>
          )}
          {form.type === 'overhead' && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Overhead category</label>
              <OverheadCategorySelect
                categories={overheads}
                value={form.overhead_category_id}
                onChange={v => set('overhead_category_id', v)}
              />
            </div>
          )}

          {/* Hours */}
          <div data-tour="entry-hours">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Hours</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {[0.5, 1, 1.5, 2, 3, 4].map(h => (
                <button key={h} type="button" onClick={() => set('hours', String(h))}
                  className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                    parseFloat(form.hours) === h ? 'bg-navy border-navy text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {h}h
                </button>
              ))}
              <button type="button" onClick={() => set('hours', String(cap))}
                title={`Log a full ${fmt(cap)} day`}
                className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                  parseFloat(form.hours) === cap ? 'bg-navy border-navy text-white' : 'bg-white border-blue-200 text-blue-600 hover:bg-blue-50'
                }`}>
                Full day ({fmt(cap)})
              </button>
              {remainder > 0 && remainder !== cap && (
                <button type="button" onClick={() => set('hours', String(remainder))}
                  title={`Fill the rest of your ${fmt(cap)} day`}
                  className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                    parseFloat(form.hours) === remainder ? 'bg-navy border-navy text-white' : 'bg-white border-blue-200 text-blue-600 hover:bg-blue-50'
                  }`}>
                  Rest of day ({fmt(remainder)})
                </button>
              )}
            </div>
            <input type="number" step="0.25" min="0.25" max="24" className="form-input"
              value={form.hours} onChange={e => set('hours', e.target.value)} placeholder="Hours…" />
          </div>

          {/* Notes */}
          <div data-tour="entry-notes">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {isOther ? 'Notes (required for Other)'
                : milestoneUnset ? 'Notes (reason required — no milestone)'
                : 'Notes (optional)'}
            </label>
            <input type="text" maxLength={200}
              className={`form-input ${(isOther && !(form.description || '').trim()) || needsMilestoneReason ? 'border-amber-400 focus:ring-amber-400' : ''}`}
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder={isOther ? 'Say what this was…'
                : milestoneUnset ? 'Why does this belong to no milestone?'
                : 'What did you work on?'} />
          </div>

          {error && <div className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </div>
    </Modal>
  );
}

// ── Breakdown modal ────────────────────────────────────────────────────────────
//
// The summary bar gives four figures and the grid gives the entries; between
// them nothing totals a single job across the week. This is that: a row per
// task, project and overhead category, a column per day, totals down the right
// and along the bottom. The grouping comes from utils/timeBreakdown.js, which is
// the same one the summary tiles use, so the two reconcile.
//
// Past a week's worth of columns the matrix stops being readable, so a month
// shows the totals column only and says to switch to Week for the day detail.
// Thirty-one columns behind a horizontal scrollbar is not a view of a month.
const GROUP_TINT = {
  tasks:     { text: 'text-blue-700',    bar: 'bg-blue-400'    },
  projects:  { text: 'text-emerald-700', bar: 'bg-emerald-400' },
  overheads: { text: 'text-amber-700',   bar: 'bg-amber-400'   },
};

function BreakdownModal({ breakdown, periodLabel, showDays, onClose }) {
  const { days, groups, byDay, total } = breakdown;
  // The label column is sticky so a narrow screen can scroll the days sideways
  // without losing which row it is reading.
  const stickyCell = 'sticky left-0 z-10 bg-white';
  const cell = (v) => v ? fmt(v) : <span className="text-gray-300">&mdash;</span>;

  return (
    <Modal title={`Breakdown — ${periodLabel}`} onClose={onClose} xl>
      {groups.length === 0 ? (
        <div className="py-10 text-center">
          <div className="text-[12px] font-semibold text-gray-600">No time logged in this period</div>
          <div className="text-[10px] text-gray-400 mt-1">Log some time and it will be broken down here.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className={`${stickyCell} text-left font-semibold text-gray-400 uppercase tracking-wide text-[9px] py-1.5 pr-3`}>
                  What
                </th>
                {showDays && days.map(d => (
                  <th key={d} className="px-2 py-1.5 text-center font-semibold text-gray-400 uppercase tracking-wide text-[9px] w-[52px]">
                    <div>{fmtDow(d)}</div>
                    <div className="text-gray-500 text-[10px] font-bold">{fmtDayNum(d)}</div>
                  </th>
                ))}
                <th className="pl-3 py-1.5 text-right font-semibold text-gray-400 uppercase tracking-wide text-[9px] w-[64px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.key}>
                  <tr className="border-t border-gray-100">
                    <td className={`${stickyCell} pt-3 pb-1 pr-3`}>
                      <span className={`text-[10px] font-bold uppercase tracking-wide ${GROUP_TINT[g.key].text}`}>{g.label}</span>
                    </td>
                    {showDays && days.map(d => <td key={d} />)}
                    <td className={`pl-3 pt-3 pb-1 text-right text-[11px] font-bold tabular-nums ${GROUP_TINT[g.key].text}`}>
                      {fmt(g.total)}
                    </td>
                  </tr>
                  {g.rows.map(r => (
                    <tr key={r.key} className="group hover:bg-gray-50">
                      <td className={`${stickyCell} group-hover:bg-gray-50 py-1 pr-3 max-w-[260px]`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${GROUP_TINT[g.key].bar}`} />
                          <span className="min-w-0">
                            <span className="block text-[11px] font-semibold text-gray-800 truncate">{r.label}</span>
                            {r.sublabel && <span className="block text-[9px] text-gray-400 truncate">{r.sublabel}</span>}
                          </span>
                        </div>
                      </td>
                      {showDays && days.map(d => (
                        <td key={d} className="px-2 py-1 text-center text-[11px] text-gray-700 tabular-nums">{cell(r.byDay[d])}</td>
                      ))}
                      <td className="pl-3 py-1 text-right text-[11px] font-bold text-gray-800 tabular-nums">{fmt(r.total)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300">
                <td className={`${stickyCell} py-2 pr-3 text-[11px] font-bold text-navy`}>Total</td>
                {showDays && days.map(d => (
                  <td key={d} className="px-2 py-2 text-center text-[11px] font-bold text-navy tabular-nums">{cell(byDay[d])}</td>
                ))}
                <td className="pl-3 py-2 text-right text-[12px] font-bold text-navy tabular-nums">{fmt(total)}</td>
              </tr>
            </tfoot>
          </table>
          {!showDays && (
            <div className="mt-3 text-[10px] text-gray-500">
              Totals for the whole period. Switch to Week for the day-by-day columns.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MyTimePage() {
  const { currentUser } = useApp();
  const [view, setView] = useState('week');
  const [refDate, setRefDate] = useState(today);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  // Set once the first load has come back, whether or not it found anything.
  // The tour is held until then, so it does not open onto a grid that has yet
  // to draw the columns its steps point at.
  const [loaded, setLoaded] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [sows, setSows] = useState([]);
  const [overheads, setOverheads] = useState([]);
  // Milestones for the project currently chosen in the Add/Edit form. Held here
  // rather than in the modal because the save path applies the rule about
  // logging with no milestone, and it needs to know whether there was one.
  const [formMilestones, setFormMilestones] = useState([]);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // Edit cut-off: a week closes when the following Monday ends, slid over bank
  // holidays; holders of time:manage (admin, HR and PMO by default) bypass. It
  // is the key canManageTime in src/api/router.js asks, and a test checks both
  // ask it, so a PMO user is not shown padlocks on controls the server would accept.
  //
  // Recent bank holidays are loaded because they slide the boundary. Until they
  // arrive (or if the fetch fails) the plain-Monday rule applies, which only
  // ever locks EARLIER than the server, so the worst case is a padlock the
  // server would accept — never an entry typed and then refused.
  const lockBypass = canAccess('time:manage');
  const [recentBankHols, setRecentBankHols] = useState(() => new Set());
  useEffect(() => {
    api.get('bank-holidays', { from: addDays(today, -21), to: today })
      .then(rows => setRecentBankHols(new Set(
        (rows || []).filter(h => h.kind === 'bank_holiday').map(h => h.holiday_date)
      )))
      .catch(() => {});
  }, []);
  const isBankHol = useCallback((d) => recentBankHols.has(d), [recentBankHols]);
  const editCutoff = earliestEditable(today, isBankHol);   // earliest editable date
  const isDateLocked = useCallback(
    (d) => !lockBypass && isTimeEntryLocked(d, today, isBankHol),
    [lockBypass, isBankHol],
  );
  const { favourites, sowFavourites, has: isFav, toggle: toggleFav } = useTimeFavourites(currentUser?.id);

  // ── Period ──────────────────────────────────────────────────────────────────
  const { from, to, periodLabel, days, monthDays } = useMemo(() => {
    if (view === 'day') {
      return { from: refDate, to: refDate, periodLabel: fmtLong(refDate), days: [refDate], monthDays: null };
    }
    if (view === 'week') {
      const weekStart = getMondayOf(new Date(refDate + 'T00:00:00'));
      const ds = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      const year = new Date(weekStart + 'T00:00:00').getFullYear();
      // "to" rather than a dash, matching Bookings (fmtRange) and the house
      // style: dashes are for number ranges in tables, not for prose.
      return { from: weekStart, to: ds[6], periodLabel: `${fmtShort(weekStart)} to ${fmtShort(ds[6])} ${year}`, days: ds, monthDays: null };
    }
    // month — build full calendar grid (Mon-Sun weeks)
    const mStart = getMonthStart(new Date(refDate + 'T00:00:00'));
    const mEnd   = getMonthEnd(new Date(refDate + 'T00:00:00'));
    const startDow = new Date(mStart + 'T00:00:00').getDay();
    const gridStart = addDays(mStart, startDow === 0 ? -6 : 1 - startDow);
    const endDow = new Date(mEnd + 'T00:00:00').getDay();
    const gridEnd = endDow === 0 ? mEnd : addDays(mEnd, 7 - endDow);
    const numDays = Math.round((new Date(gridEnd + 'T00:00:00') - new Date(gridStart + 'T00:00:00')) / 86400000) + 1;
    const allDays = Array.from({ length: numDays }, (_, i) => addDays(gridStart, i));
    return { from: mStart, to: mEnd, periodLabel: fmtMonth(refDate), days: allDays, monthDays: { mStart, mEnd } };
  }, [view, refDate]);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const data = await api.get('time-entries', { person_id: currentUser.id, from, to });
      setEntries(data || []);
    } catch { setEntries([]); } finally { setLoading(false); setLoaded(true); }
  }, [currentUser?.id, from, to]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // The guided tour, for people arriving from an external timesheet. Auto-opens
  // the first time only (the server remembers finished/skipped per person); the
  // Tour button in the header replays it afterwards.
  const tour = useFeatureTour(TIME_LOGGING_TOUR_ID, { enabled: loaded });
  // The steps describe the week grid, so start the tour looking at it.
  useEffect(() => { if (tour.open) setView('week'); }, [tour.open]);

  // ── Days already accounted for (El's item 44) ───────────────────────────────
  //
  // "Can this link up with sickness and annual leave so it automatically applies
  // absences?" — and El's answer on what it should write: "Fills the day without
  // counting as work."
  //
  // So nothing is written. Approved leave, sickness and bank holidays are read as
  // FACTS and used to stop this screen calling a day short when it is a day the
  // person was not working. A day off logged as hours would flow into utilisation
  // and project cost, where it would read as work done.
  const [absences, setAbsences] = useState({});
  useEffect(() => {
    if (!currentUser?.id) { setAbsences({}); return; }
    api.get('absences', { person_id: currentUser.id, from, to })
      .then(rows => {
        const m = {};
        for (const a of (rows || [])) m[a.date] = a;
        setAbsences(m);
      })
      .catch(() => setAbsences({}));
  }, [currentUser?.id, from, to]);
  // Refresh the grid the moment time is logged anywhere (e.g. the Quick Time
  // widget), without a page refresh.
  useEffect(() => {
    const h = () => loadEntries();
    window.addEventListener('av:time-entries-changed', h);
    return () => window.removeEventListener('av:time-entries-changed', h);
  }, [loadEntries]);
  // Announce a change so this grid and the Quick Time widget both refresh.
  const bumpTime = () => window.dispatchEvent(new CustomEvent('av:time-entries-changed'));

  useEffect(() => {
    if (!currentUser?.id) return;
    Promise.all([
      api.get('tasks/mine').catch(() => []),
      api.get('sows', { status: 'Active' }).catch(() => []),
      // Draft SOWs are included too — lets bid/SOW-creation time be tracked
      // against the specific SOW before it's ever issued (see EntryModal).
      api.get('sows', { status: 'Draft' }).catch(() => []),
      api.get('overhead-categories').catch(() => []),
      // Awaiting Payment too: invoice queries and snagging still take time.
      api.get('sows', { status: 'Awaiting Payment' }).catch(() => []),
    ]).then(([t, active, draft, o, awaitingPayment]) => { setTasks(t || []); setSows([...(active || []), ...(draft || []), ...(awaitingPayment || [])]); setOverheads(o || []); });
  }, [currentUser?.id]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const navPeriod = (dir) => {
    const d = new Date(refDate + 'T00:00:00');
    if (view === 'day')   d.setDate(d.getDate() + dir);
    if (view === 'week')  d.setDate(d.getDate() + dir * 7);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    setRefDate(toLocalISO(d));
    setSelectedDays(new Set());
  };

  // ── Selection ───────────────────────────────────────────────────────────────
  const toggleDay = (dateStr) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
      return next;
    });
  };

  // ── Entry map ────────────────────────────────────────────────────────────────
  const entryMap = useMemo(() => {
    const m = {};
    for (const e of entries) {
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    }
    return m;
  }, [entries]);

  const totalHours = entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const byType = useMemo(() => ({
    tasks:     entries.filter(e => e.task_id).reduce((s, e) => s + (parseFloat(e.hours)||0), 0),
    sows:      entries.filter(e => e.sow_id && !e.task_id).reduce((s, e) => s + (parseFloat(e.hours)||0), 0),
    overheads: entries.filter(e => !e.task_id && !e.sow_id).reduce((s, e) => s + (parseFloat(e.hours)||0), 0),
  }), [entries]);

  // ── Breakdown (the dialog behind the Breakdown button) ─────────────────────────────
  //
  // A month's `days` is the whole calendar GRID, which runs into the months
  // either side; those days carry no entries (the query is the month) but they
  // would draw as empty columns, so the breakdown is built over the month
  // itself. Past a week the day columns are dropped altogether — see
  // BreakdownModal.
  const breakdownDays = useMemo(
    () => monthDays ? days.filter(d => d >= monthDays.mStart && d <= monthDays.mEnd) : days,
    [days, monthDays],
  );
  const breakdown = useMemo(() => buildBreakdown(entries, breakdownDays), [entries, breakdownDays]);

  // A Draft SOW has no milestones to bill against yet — its time is Bid & SOW
  // Creation — so nothing is fetched for one, and the question is not asked.
  const formSowIsDraft = form.type === 'sow'
    && sows.find(x => String(x.id) === String(form.sow_id))?.status === 'Draft';
  useEffect(() => {
    if (form.type !== 'sow' || !form.sow_id || formSowIsDraft) { setFormMilestones([]); return; }
    let live = true;
    api.get('milestones', { sow_id: form.sow_id })
      .then(m => { if (live) setFormMilestones(Array.isArray(m) ? m : []); })
      .catch(() => { if (live) setFormMilestones([]); });
    return () => { live = false; };
  }, [form.type, form.sow_id, formSowIsDraft]);

  // ── Modal helpers ────────────────────────────────────────────────────────────
  const openAdd = (initDates = []) => {
    let dates = initDates.length ? initDates : selectedDays.size ? [...selectedDays].sort() : [today];
    dates = dates.filter(d => !isDateLocked(d));
    if (!dates.length) { alert(`Those dates are in a closed week. A week closes at the end of the following Monday; you can add or amend time from ${fmtShort(editCutoff)} onwards.`); return; }
    setForm({ type: 'task', task_id: '', sow_id: '', overhead_category_id: '', milestone_id: '', hours: '1', description: '', dates });
    setFormError('');
    setModal({ entry: null });
  };

  const openEdit = (entry) => {
    if (isDateLocked(entry.date)) { alert(`This entry is in a closed week and can no longer be amended. A week closes at the end of the following Monday; you can still change anything from ${fmtShort(editCutoff)} onwards.`); return; }
    setForm({
      type: entry.task_id ? 'task' : (entry.sow_id ? 'sow' : 'overhead'),
      task_id: entry.task_id || '',
      sow_id: entry.sow_id || '',
      overhead_category_id: entry.overhead_category_id || '',
      milestone_id: entry.milestone_id || '',
      hours: String(entry.hours || 1),
      description: entry.description || '',
      dates: [entry.date],
    });
    setFormError('');
    setModal({ entry });
  };

  const closeModal = () => { setModal(null); setFormError(''); };

  // Target payload (task/sow/overhead) shared across all posts. For a Draft SOW,
  // EntryModal auto-populates overhead_category_id (Bid & SOW Creation) into the
  // form — sent alongside sow_id so the hours are both categorised correctly and
  // still roll up into that SOW's own cost.
  const targetPayload = () => ({
    source: 'my-time',
    ...(form.type === 'task'     ? { task_id: form.task_id }                            : {}),
    ...(form.type === 'sow'      ? { sow_id: form.sow_id, milestone_id: form.milestone_id || null, overhead_category_id: form.overhead_category_id || null, billable: form.billable } : {}),
    ...(form.type === 'overhead' ? { overhead_category_id: form.overhead_category_id } : {}),
  });


  const validateForm = () => {
    const h = parseFloat(form.hours);
    if (!h || h <= 0 || h > 24) { setFormError('Enter valid hours (0.25 – 24)'); return null; }
    if (form.type === 'task' && !form.task_id) { setFormError('Select a task'); return null; }
    if (form.type === 'sow' && !form.sow_id) { setFormError('Select a project'); return null; }
    if (form.type === 'overhead' && !form.overhead_category_id) { setFormError('Select a category'); return null; }
    if (!form.dates?.length) { setFormError('Select at least one date'); return null; }
    // "Other" says nothing on its own. The quick panel has always refused this;
    // the same entry used to go straight through here, so which screen you
    // happened to open decided whether the rule applied. Same rule, one source.
    if (form.type === 'overhead' && isOtherOverhead(overheads, form.overhead_category_id)
        && !(form.description || '').trim()) {
      setFormError(OTHER_NOTE_MESSAGE);
      return null;
    }
    // Hours on a milestoned project with no milestone cost against nothing. If
    // that is deliberate, say why, so the entry explains itself on the audit
    // rather than becoming somebody else's guesswork. Same rule as the quick panel.
    if (form.type === 'sow' && !formSowIsDraft
        && missingMilestoneReason({ milestones: formMilestones, milestoneId: form.milestone_id, notes: form.description })) {
      setFormError(NO_MILESTONE_REASON_MESSAGE);
      return null;
    }
    return h;
  };

  const handleSave = async () => {
    const h = validateForm();
    if (h == null) return;
    const base = { description: form.description || null, ...targetPayload() };

    // Edits: update in place, no overtime split
    if (modal?.entry) {
      setSaving(true); setFormError('');
      try {
        await api.put(`time-entries/${modal.entry.id}`, { ...base, hours: h, date: form.dates[0] });
        closeModal(); setSelectedDays(new Set()); bumpTime();
      } catch (e) { setFormError(e.message || 'Failed to save'); } finally { setSaving(false); }
      return;
    }

    // New entries: log the hours as-is for each selected date (no overtime split).
    await postPlan(form.dates.map(date => ({ date, hours: h })));
  };

  // Post a plan: [{ date, hours }]
  const postPlan = async (datePlan) => {
    setSaving(true); setFormError('');
    const base = { description: form.description || null, ...targetPayload() };
    try {
      for (const { date, hours } of datePlan) {
        await api.post('time-entries', { ...base, date, hours });
      }
      closeModal(); setSelectedDays(new Set()); bumpTime();
    } catch (e) {
      setFormError(e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (entry) => {
    if (isDateLocked(entry.date)) { alert(`This entry is in a closed week and can no longer be deleted. A week closes at the end of the following Monday; you can still change anything from ${fmtShort(editCutoff)} onwards.`); return; }
    if (!window.confirm(`Delete ${fmt(parseFloat(entry.hours))} entry for ${entryLabel(entry)}?`)) return;
    try { await api.delete(`time-entries/${entry.id}`); bumpTime(); }
    catch (e) { alert(e.message || 'Delete failed'); }
  };

  // ── Render helpers ────────────────────────────────────────────────────────────

  const renderDayView = () => {
    const dayEntries = entryMap[refDate] || [];
    const dayTotal = dayEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
    const locked = isDateLocked(refDate);
    return (
      <div className="space-y-2">
        {dayTotal > 0 && (
          <div className="text-[11px] text-gray-500 font-semibold">{fmt(dayTotal)} logged</div>
        )}
        {dayEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No entries for this day.</div>
        ) : (
          dayEntries.map(e => <EntryCard key={e.id} entry={e} onEdit={openEdit} onDelete={handleDelete} locked={locked} />)
        )}
        {locked ? (
          <div className="w-full py-2.5 text-center text-[11px] font-semibold text-gray-300 select-none">🔒 Closed — this week ended</div>
        ) : (
          <button onClick={() => openAdd([refDate])}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-[11px] font-semibold text-gray-400 hover:border-navy hover:text-navy transition-colors">
            + Add Entry
          </button>
        )}
      </div>
    );
  };

  const renderWeekView = () => {
    const cap = dailyCap(currentUser);
    // Weekdays this week that are here/past and under a full day, and not locked.
    const missingDays = days.filter(dateStr => {
      const dow = new Date(dateStr + 'T00:00:00').getDay();
      if (dow < 1 || dow > 5 || dateStr > today || isDateLocked(dateStr)) return false;
      // Item 44: a day of leave, sickness or a bank holiday is not a day with
      // time missing from it. Chasing somebody for the week they were on holiday
      // is the reason this was asked for. A HALF day off excuses only its half:
      // the other half is still expected, at half the daily cap.
      const absence = absences[dateStr];
      const halfAbsence = absence?.slot === 'am' || absence?.slot === 'pm';
      if (absence && !halfAbsence) return false;
      const t = workedHours(entryMap[dateStr]);
      return t < (halfAbsence ? cap / 2 : cap) - 0.001;
    });
    return (
      <div>
        {missingDays.length > 0 && (
          <div data-tour="missing-days" className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
            <span className="text-[10px] font-semibold text-amber-800">
              {missingDays.length} weekday{missingDays.length > 1 ? 's' : ''} this week {missingDays.length > 1 ? 'are' : 'is'} under a full day.
            </span>
            <button onClick={() => openAdd(missingDays)}
              className="text-[10px] font-bold text-amber-950 bg-amber-500 hover:bg-amber-600 px-2.5 py-1 rounded whitespace-nowrap">
              Log the missing days →
            </button>
          </div>
        )}
        <div className="grid grid-cols-7 gap-2 overflow-x-auto">
          {days.map(dateStr => (
            <DayColumn
              key={dateStr}
              dateStr={dateStr}
              entries={entryMap}
              selected={selectedDays.has(dateStr)}
              onToggle={toggleDay}
              onAdd={openAdd}
              onEdit={openEdit}
              onDelete={handleDelete}
              locked={isDateLocked(dateStr)}
              cap={cap}
              absence={absences[dateStr] || null}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const { mStart, mEnd } = monthDays;
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return (
      <div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
            <div key={d} className="text-center text-[9px] font-bold text-gray-400 uppercase py-1">{d}</div>
          ))}
        </div>
        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
            {week.map(dateStr => {
              const inMonth = dateStr >= mStart && dateStr <= mEnd;
              const dayEntries = entryMap[dateStr] || [];
              const dayTotal = dayEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
              const isToday = dateStr === today;
              const sel = selectedDays.has(dateStr);
              return (
                <div
                  key={dateStr}
                  onClick={() => toggleDay(dateStr)}
                  onDoubleClick={() => openAdd([dateStr])}
                  className={`rounded-lg p-1.5 min-h-[68px] cursor-pointer border transition-colors ${
                    !inMonth ? 'opacity-30 bg-gray-50 border-transparent' :
                    sel ? 'bg-navy border-navy' :
                    isToday ? 'bg-blue-50 border-blue-200' :
                    'bg-white border-gray-100 hover:border-gray-300'
                  }`}
                >
                  <div className={`text-[11px] font-bold mb-1 ${sel ? 'text-white' : isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                    {fmtDayNum(dateStr)}
                  </div>
                  {dayTotal > 0 && (
                    <div className={`text-[9px] font-semibold mb-0.5 ${sel ? 'text-white/80' : 'text-navy'}`}>{fmt(dayTotal)}</div>
                  )}
                  <div className="space-y-0.5">
                    {/* Item 44: the day reads as accounted for, with no hours
                        against it. */}
                    {absences[dateStr] && (
                      <div className={`text-[8px] truncate px-1 py-0.5 rounded font-semibold ${
                        sel ? 'bg-white/20 text-white'
                        : absences[dateStr].kind === 'sickness' ? 'bg-red-50 text-red-700'
                        : absences[dateStr].kind === 'bank_holiday' ? 'bg-gray-100 text-gray-500'
                        : 'bg-blue-50 text-blue-700'}`}>
                        {absences[dateStr].label}
                      </div>
                    )}
                    {dayEntries.slice(0, 2).map(e => {
                      const c = typeColor(e);
                      return (
                        <div key={e.id} className={`text-[8px] truncate px-1 py-0.5 rounded ${sel ? 'bg-white/20 text-white' : `${c.bg} ${c.text}`}`}>
                          {fmt(parseFloat(e.hours))} {entryLabel(e)}
                        </div>
                      );
                    })}
                    {dayEntries.length > 2 && (
                      <div className={`text-[8px] font-semibold ${sel ? 'text-white/60' : 'text-gray-400'}`}>+{dayEntries.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div className="mt-2 text-[9px] text-gray-400 text-center">Click to select days · Double-click to add entry</div>
      </div>
    );
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          {/* Replay the walkthrough. Always available, so "it only shows once"
              never strands somebody who skipped it and regrets it.

              A circled ? against the heading, which is where people look for
              "explain this screen". Identical to the one on Bookings, down to
              the pixel size and the label: the two pages arrived here
              separately, and a help control that differs slightly between
              screens is two conventions rather than one. A test pins them
              together. */}
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-navy">My Time</h2>
            <button
              type="button"
              onClick={tour.start}
              aria-label="Replay the guided tour of this page"
              title="Replay the guided tour of this page"
              className="w-[23px] h-[23px] shrink-0 rounded-full border border-gray-300 bg-white
                text-gray-500 text-[12px] font-semibold leading-none flex items-center justify-center
                transition-colors hover:border-navy hover:text-navy hover:bg-gray-50"
            >
              ?
            </button>
          </div>
          <p className="text-[10px] text-gray-500 mt-0.5">Log, view and manage your time entries</p>
        </div>
        <button
          data-tour="add-entry"
          onClick={() => openAdd()}
          className="px-3 py-2 rounded-md border font-semibold text-[11px] bg-navy text-white border-navy hover:bg-navy/90 transition-colors"
        >
          {selectedDays.size > 0 ? `+ Add Entry (${selectedDays.size} day${selectedDays.size > 1 ? 's' : ''} selected)` : '+ Add Entry'}
        </button>
      </div>

      {/* Controls, laid out like Bookings: arrows and the date on the left, the
          view switch beside them, Today next to the dates rather than pushed to
          the far edge — these are the controls for the period you are looking
          at, and ml-auto leaves a metre of empty header between them and it. */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => navPeriod(-1)} aria-label="Previous period"
            className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 leading-none">‹</button>
          <button type="button" onClick={() => navPeriod(1)} aria-label="Next period"
            className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 leading-none">›</button>
        </div>

        <span className="text-[13px] font-bold text-gray-800">{periodLabel}</span>

        <Btn onClick={() => { setRefDate(today); setSelectedDays(new Set()); }}>Today</Btn>

        <div data-tour="view-toggle" className="flex gap-1.5 flex-wrap">
          {['day','week','month'].map(v => (
            <Btn key={v} active={view === v} onClick={() => { setView(v); setSelectedDays(new Set()); }}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </Btn>
          ))}
        </div>

        {/* Where the week actually went, totalled per job. The summary tiles
            below say how much was project time; this says which projects. */}
        <Btn data-tour="breakdown" onClick={() => setBreakdownOpen(true)}>Breakdown</Btn>

        {selectedDays.size > 0 && (
          <button onClick={() => setSelectedDays(new Set())}
            className="text-[10px] text-gray-500 hover:text-red-500 font-semibold px-2 py-1.5">
            Clear selection
          </button>
        )}

        {loading && <span className="text-[10px] text-gray-500">Loading…</span>}
      </div>

      {/* Summary bar */}
      <div data-tour="summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Total', value: totalHours, color: 'text-navy' },
          { label: 'Tasks', value: byType.tasks, color: 'text-blue-600' },
          { label: 'Projects', value: byType.sows, color: 'text-emerald-600' },
          { label: 'Overheads', value: byType.overheads, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
            {/* tabular-nums so the figures do not jitter as the hours change */}
            <div className={`text-[18px] font-bold tabular-nums ${s.color}`}>{fmt(s.value)}</div>
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Calendar/List */}
      <div data-tour="calendar" className="bg-white border border-gray-200 rounded-lg p-3">
        {view === 'day'   && renderDayView()}
        {view === 'week'  && renderWeekView()}
        {view === 'month' && renderMonthView()}
      </div>

      {/* Modal */}
      {modal !== null && (
        <EntryModal
          form={form}
          setForm={setForm}
          onSave={handleSave}
          onClose={closeModal}
          saving={saving}
          error={formError}
          tasks={tasks}
          sows={sows}
          overheads={overheads}
          milestones={formMilestones}
          isEdit={!!modal.entry}
          minDate={lockBypass ? undefined : editCutoff}
          entryMap={entryMap}
          cap={dailyCap(currentUser)}
          excludeEntryId={modal.entry?.id}
          favourites={favourites}
          sowFavourites={sowFavourites}
          isFav={isFav}
          toggleFav={toggleFav}
        />
      )}

      {breakdownOpen && (
        <BreakdownModal
          breakdown={breakdown}
          periodLabel={periodLabel}
          showDays={breakdownDays.length <= 7}
          onClose={() => setBreakdownOpen(false)}
        />
      )}

      {tour.open && (
        <FeatureTour steps={timeLoggingTourSteps} onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}
